import React, {useState} from 'react';
import {motion} from 'framer-motion';
import {useTranslation} from 'react-i18next';
import Seo from '../../../../shared/seo/Seo';
import {
    FaMapMarkerAlt,
    FaPhone,
    FaHome,
    FaEnvelope,
    FaClock,
    FaCheck,
    FaFacebookF,
    FaInstagram,
    FaLinkedinIn,
    FaUser,
    FaCommentAlt,
    FaBuilding,
    FaPaperPlane
} from 'react-icons/fa';
import MapComponent from '../../../common/MapComponent';
import PageHeader from '../../../common/PageHeader';
import config from '../../../../config/config';
import { sendQuoteRequestEmail, QuoteFormData } from '../../../../services/quoteService';
import { getCanonicalUrl, pageMeta } from 'shared/seo/seoMeta';
import './Contact.css';

interface FormData {
    name: string;
    email: string;
    phone: string;
    subject: string;
    service: string;
    message: string;
}

interface FormErrors {
    [key: string]: string | null;
}

const Contact: React.FC = () => {
    const {t} = useTranslation();
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        phone: '',
        subject: '',
        service: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [errors, setErrors] = useState<FormErrors>({});

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
        const {name, value} = e.target;
        setFormData(prev => ({...prev, [name]: value}));

        // Clear error when field is edited
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: null}));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = t('contact.form.nameRequired');
        }

        if (!formData.email.trim()) {
            newErrors.email = t('contact.form.emailRequired');
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = t('contact.form.emailInvalid');
        }

        if (!formData.message.trim()) {
            newErrors.message = t('contact.form.messageRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();

        if (validateForm()) {
            setIsSubmitting(true);

            // Map contact form data to quote request format
            const quoteData: QuoteFormData = {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                service: formData.service,
                subservice: formData.subject,
                message: formData.message
            };
            
            const isSuccessful = await sendQuoteRequestEmail(quoteData);
            setIsSubmitting(false);
            
            if (isSuccessful) {
                setIsSubmitted(true);
                // Reset form after submission
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    subject: '',
                    service: '',
                    message: ''
                });
                
                // Reset success message after 5 seconds
                setTimeout(() => {
                    setIsSubmitted(false);
                }, 5000);
            } else {
                // Handle error - show error message to user if needed
                console.error('Failed to send contact form email');
                // You might want to add an error state here to inform the user
            }
        }
    };

    //add here geo info : to use google maps
    const openGoogleMaps = () => {
        const latitude = config.map.lat;
        const longitude = config.map.lng;
        const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
        window.open(url, '_blank');
    };

    return (
        <>
            <Seo
              title={pageMeta.contact.title}
              description={pageMeta.contact.description}
              canonical={getCanonicalUrl(pageMeta.contact.canonicalPath)}
            />
            <PageHeader
                title={t('contact.title')}
                subtitle={t('contact.subtitle')}
                background="/images/background/contact-bg.webp"
            />

            <section className="contact-section">
                <div className="container">
                    <div className="contact-grid">
                        <motion.div
                            className="contact-form-container"
                            initial={{opacity: 0, y: 30}}
                            whileInView={{opacity: 1, y: 0}}
                            transition={{duration: 0.6}}
                            viewport={{once: true}}
                        >
                            <form className="contact-form" onSubmit={handleSubmit}>
                                <h3 className="form-title">{t('contact.form.title')}</h3>

                                <div className="form-row">
                                    <div>
                                        <div className={`form-input-wrapper ${errors.name ? 'error' : ''}`}>
                        <span className={`form-icon-contact ${formData.name ? 'active' : ''}`}>
                          <FaUser/>
                        </span>

                                            <input
                                                type="text"
                                                name="name"
                                                className={`form-input ${errors.name ? 'error' : ''}`}
                                                placeholder={t('contact.form.name', 'Your Name')}
                                                value={formData.name}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        {errors.name && <div className="error-message">{errors.name}</div>}
                                    </div>

                                    <div>
                                        <div className={`form-input-wrapper ${errors.email ? 'error' : ''}`}>
                        <span className={`form-icon-contact ${formData.email ? 'active' : ''}`}>
                          <FaEnvelope/>
                        </span>
                                            <input
                                                type="email"
                                                name="email"
                                                className={`form-input ${errors.email ? 'error' : ''}`}
                                                placeholder={t('contact.form.email', 'Your Email')}
                                                value={formData.email}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        {errors.email && <div className="error-message">{errors.email}</div>}
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div>
                                        <div className="form-input-wrapper">
                        <span className={`form-icon-contact ${formData.phone ? 'active' : ''}`}>
                          <FaPhone/>
                        </span>
                                            <input
                                                type="tel"
                                                name="phone"
                                                className="form-input"
                                                placeholder={t('contact.form.phone', 'Your Phone')}
                                                value={formData.phone}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                

                                    <div>
                                        <div className="form-input-wrapper">
                        <span className={`form-icon-contact ${formData.subject ? 'active' : ''}`}>
                          <FaCommentAlt/>
                        </span>
                                            <input
                                                type="text"
                                                name="subject"
                                                className="form-input"
                                                placeholder={t('contact.form.subject', 'Subject')}
                                                value={formData.subject}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div className="form-input-wrapper">
                      <span className={`form-icon-contact ${formData.service ? 'active' : ''}`}>
                        <FaBuilding/>
                      </span>
                                        <select
                                            name="service"
                                            className="form-input"
                                            value={formData.service}
                                            onChange={handleChange}
                                        >
                                            <option
                                                value="">{t('contact.form.selectService', 'Select Service')}</option>
                                            <option
                                                value="maintenance">{t('services.categories.maintenance', 'Maintenance Industrielle')}</option>
                                            <option
                                                value="waterTreatment">{t('services.categories.waterTreatment', 'Traitement de l\'eau')}</option>
                                            <option
                                                value="pools">{t('services.categories.pools', 'Fourniture et installation des piscines')}</option>
                                            <option
                                                value="renovation">{t('services.categories.renovation', 'Renovation des bâtiments industriels et habitation')}</option>
                                            <option
                                                value="industrialSupply">{t('services.categories.industrialSupply', 'Fourniture Industrielle')}</option>
                                            <option
                                                value="airCompressors">{t('services.categories.airCompressors', 'Compresseurs d\'Air')}</option>
                                            <option
                                                value="other">{t('services.categories.other', 'Autres Services')}</option>
                                        </select>
                                    </div>
                                </div>

                                <div style={{marginTop: '20px'}}>
                                <div className={`form-textarea-wrapper ${errors.message ? 'error' : ''}`}>
                      <span className={`form-icon-contact ${formData.message ? 'active' : ''}`}>
                        <FaPaperPlane/>
                      </span>
                                        <textarea
                                            name="message"
                                            className={`form-textarea ${errors.message ? 'error' : ''}`}
                                            placeholder={t('contact.form.message', 'Your Message')}
                                            value={formData.message}
                                            onChange={handleChange}
                                        ></textarea>
                                    </div>
                                    {errors.message && <div className="error-message">{errors.message}</div>}
                                </div>

                                <button
                                    type="submit"
                                    className="submit-button"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="loading-spinner"></span>
                                            <span>{t('contact.form.sending', 'Envoi en cours...')}</span>
                                        </>
                                    ) : (
                                        <span>{t('contact.form.submit', 'Envoyer Message')}</span>
                                    )}
                                </button>
                                {isSubmitted ? (
                                    <div className="success-message" style={{marginTop: '20px'}}>
                                        <div className="success-icon">
                                            <FaCheck/>
                                        </div>
                                        <h3 className="success-title">{t('contact.form.success')}</h3>
                                        <p className="success-text">
                                            {t('contact.form.successMessage')}
                                        </p>
                                    </div>
                                ) : null} 
                            </form>
                        </motion.div>

                        <motion.div
                            className="contact-info-container"
                            initial={{opacity: 0, y: 30}}
                            whileInView={{opacity: 1, y: 0}}
                            transition={{duration: 0.6, delay: 0.2}}
                            viewport={{once: true}}
                        >
                            <h3 className="info-title">{t('contact.info.title', 'Contact Information')}</h3>
                            <p className="info-subtitle">
                                {t('contact.info.subtitle', 'Feel free to contact us anytime. We will get back to you as soon as possible.')}
                            </p>

                            <div className="info-list">
                                <div className="info-item">
                                    <div className="info-icon">
                                        <FaMapMarkerAlt/>
                                    </div>
                                    <div className="info-content">
                                        <h4 className="info-label">{t('contact.info.address', 'Address')}</h4>
                                        <p className="info-text">{config.contact.address}</p>
                                    </div>
                                </div>

                                <div className="info-item">
                                    <div className="info-icon">
                                        <FaPhone/>
                                    </div>
                                    <div className="info-content">
                                        <h4 className="info-label">{t('contact.info.phone', 'Phone')}</h4>
                                        <p className="info-text" style={{direction: 'ltr', display: 'inline-block'}}>
                                            <a href={`tel:${config.contact.phone}`}>{config.contact.phone} / {config.contact.phone2}</a>
                                        </p>
                                    </div>
                                </div>

                                <div className="info-item">
                                    <div className="info-icon">
                                        <FaHome/>
                                    </div>
                                    <div className="info-content">
                                        <h4 className="info-label">{t('contact.info.fix', 'Téléphone fixe')}</h4>
                                        <p className="info-text" style={{direction: 'ltr', display: 'inline-block'}}>
                                            <a href={`tel:${config.contact.phoneFix}`}>{config.contact.phoneFix}</a>
                                        </p>
                                    </div>
                                </div>


                                <div className="info-item">
                                    <div className="info-icon">
                                        <FaEnvelope/>
                                    </div>
                                    <div className="info-content">
                                        <h4 className="info-label">{t('contact.info.email', 'Email')}</h4>
                                        <p className="info-text">
                                            <a href={`mailto:${config.contact.email}`}>{config.contact.email}</a>
                                        </p>
                                    </div>
                                </div>

                                <div className="info-item">
                                    <div className="info-icon">
                                        <FaClock/>
                                    </div>
                                    <div className="info-content">
                                        <h4 className="info-label">{t('contact.info.workHours', 'Horaires de travail')}</h4>
                                        <p className="info-text">{t('footer.contact.workHours')}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="social-links">
                                {config.contact.facebook && (
                                    <a href={config.contact.facebook} className="social-link" target="_blank"
                                       rel="noopener noreferrer">
                                        <FaFacebookF/>
                                    </a>
                                )}
                                {config.contact.instagram && (
                                    <a href={config.contact.instagram} className="social-link" target="_blank"
                                       rel="noopener noreferrer">
                                        <FaInstagram/>
                                    </a>
                                )}
                                {config.contact.linkedin && (
                                    <a href={config.contact.linkedin} className="social-link" target="_blank"
                                       rel="noopener noreferrer">
                                        <FaLinkedinIn/>
                                    </a>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <section className="map-section">
                <div className="container">
                    <motion.div
                        className="map-header"
                        initial={{opacity: 0, y: 30}}
                        whileInView={{opacity: 1, y: 0}}
                        transition={{duration: 0.6}}
                        viewport={{once: true}}
                    >
                        <h2 className="map-title">{t('contact.map.title', 'Notre Emplacement')}</h2>
                        <p className="map-subtitle">
                            {t('contact.map.subtitle', 'Trouvez-nous facilement grâce à la carte')}
                        </p>
                    </motion.div>

                    <motion.div
                        className="map-container"
                        initial={{opacity: 0, y: 30}}
                        whileInView={{opacity: 1, y: 0}}
                        transition={{duration: 0.6, delay: 0.2}}
                        viewport={{once: true}}
                    >
                        <MapComponent
                            location={[
                                config.map.lat,
                                config.map.lng
                            ]}
                            zoom={15}
                        />
                    </motion.div>

                    <div className="map-button-container">
                        <button onClick={openGoogleMaps}
                                className="btn btn-primary btn-lg d-flex align-items-center justify-content-center gap-2 mt-4 mx-auto">
                            <FaMapMarkerAlt className="map-icon"/>
                            <span>  {t('contact.openMap')}</span>
                        </button>
                    </div>
                </div>


            </section>

            <section className="faq-section" id="faq">
                <div className="container">
                    <div className="section-title">
                        <h2 className="section-title-text">{t('contact.faq.title')}</h2>
                        <p className="section-subtitle">{t('contact.faq.subtitle')}</p>
                    </div>

                    <div className="faq-list">
                        <div className="faq-item" key="faq-1">
                            <h3 className="faq-question">{t('contact.faq.q1')}</h3>
                            <p className="faq-answer">{t('contact.faq.a1')}</p>
                        </div>
                        <div className="faq-item" key="faq-2">
                            <h3 className="faq-question">{t('contact.faq.q2')}</h3>
                            <p className="faq-answer">{t('contact.faq.a2')}</p>
                        </div>
                        <div className="faq-item" key="faq-3">
                            <h3 className="faq-question">{t('contact.faq.q3')}</h3>
                            <p className="faq-answer">{t('contact.faq.a3')}</p>
                        </div>
                        <div className="faq-item" key="faq-4">
                            <h3 className="faq-question">{t('contact.faq.q4')}</h3>
                            <p className="faq-answer">{t('contact.faq.a4')}</p>
                        </div>
                    </div>

                    <div className="faq-footer">
                        <p className="faq-footer-text">{t('contact.faq.notFound')}</p>
                        <a href={`tel:${config.contact.phone}`} className="faq-footer-link">
                            <FaPhone/> {t('contact.faq.callUs')}
                        </a>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Contact;
